import { mapState } from 'vuex'
import { mask } from 'vue-the-mask'
import directives from '@/directives'
import { rutFormat } from 'rut-helpers'
import moment from 'moment'
import numeral from 'numeral'
import { capitalize, debounce, each, template, isEmpty, isArray, isString } from 'lodash'
import formatMoney from 'accounting-js/lib/formatMoney.js'
import countries from '../collections/countries'
import currencies from '../collections/currencies'

import { documentTypes } from '../collections/documents'

/** components */
import PiriodViewLoader from '@/components/ux/PiriodViewLoader'

moment.locale('es')

const frequencies = [
  {
    id: 'weekly',
    name: 'semanal',
    unit: 'semana',
    plural: 'semanas'
  },
  {
    id: 'every_fifteen_days',
    name: 'cada 15 días',
    unit: 'quincena',
    plural: 'quincenas'
  },
  {
    id: 'monthly',
    name: 'mensual',
    unit: 'mes',
    plural: 'meses'
  },
  {
    id: 'bimonthly',
    name: 'cada 2 meses',
    unit: 'bimestre',
    plural: 'bimestres'
  },
  {
    id: 'quarterly',
    name: 'cada 3 meses',
    unit: 'trimestre',
    plural: 'trimestres'
  },
  {
    id: 'biannual',
    name: 'cada 6 meses',
    unit: 'semestre',
    plural: 'semestres'
  },
  {
    id: 'annual',
    name: 'anual',
    unit: 'año',
    plural: 'años'
  }
]

export default {
  components: {
    PiriodViewLoader
  },
  directives: {
    dni: {
      update: function (el, binding, vnode) {
        let country = binding.value || 'CL'
        if (vnode.context.listenerAttached) {
          el.removeEventListener('input', vnode.context.rutFunction)
          vnode.context.listenerAttached = false
        }
        vnode.context.rutFunction = (e) => {
          vnode.componentInstance.$emit('input', country === 'CL' ? rutFormat(e.target.value) : e.target.value)
        }
        el.addEventListener('input', vnode.context.rutFunction)
        vnode.context.listenerAttached = true
      }
    },
    mask,
    ...directives
  },
  data: () => ({
    baseCountries: countries,
    baseLoader: false,
    currencies: currencies,
    currentMonth: moment().format('MMMM'),
    dropdowns: [],
    dropdownShow: false,
    frequencies: frequencies,
    listenerAttached: false,
    periods: [
      { label: '3 meses', value: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD') },
      { label: '6 meses', value: moment().subtract(5, 'months').startOf('month').format('YYYY-MM-DD') },
      { label: '12 meses', value: moment().subtract(11, 'months').startOf('month').format('YYYY-MM-DD') },
      { label: '24 meses', value: moment().subtract(23, 'months').startOf('month').format('YYYY-MM-DD') }
    ],
    rutFunction: false,
    today: moment().format('YYYY-MM-DD'),
    tomorrow: moment().add(1, 'days').format('YYYY-MM-DD')
  }),
  computed: mapState({
    account: state => state.auth.account
  }),
  filters: {
    capitalize (val) {
      return `${val.toString().charAt(0).toUpperCase()}${val.toString().slice(1)}`
    },
    currency (value, id) {
      const currency = currencies.find(c => c.id === id)
      if (!currency) {
        return value
      }
      const options = Object.assign({}, currency.format, { symbol: '' })
      return formatMoney(value, options)
    },
    date (val) {
      return moment(val).format('DD MMM YYYY')
    },
    dateConditional (val) {
      return moment(val, "hmm").format("HH:mm") > '24:00' ? moment(val).format('DD MMM YYYY hh:mm') : moment(val).fromNow()
    },
    dateFromNow (val) {
      if (!val) {
        return null
      }
      return moment(val).fromNow()
    },
    dateTime (val) {
      return moment(val).format('DD MMM YYYY HH:mm')
    },
    dateTimeCalendar (value) {
      return moment(value).calendar()
    },
    frequency (value, type = 'name') {
      const frequency = frequencies.find(c => c.id === value)
      if (!frequency) {
        return value
      }
      return frequency[type]
    },
    invoiceTypeName (id, showCountry = false) {
      const type = documentTypes.find(t => t.id === id)

      if (showCountry && type.country) {
        return `${type.name} (${type.country})`
      }

      return type.name
    },
    lower (val) {
      return val.toString().toLowerCase()
    },
    number (val, rounded = true) {
      numeral.localeData().delimiters.thousands = rounded ? '.' : ','
      numeral.localeData().delimiters.decimal = rounded ? ',' : '.'
      return numeral(val).format(rounded ? '0,0' : '0,0.00')
    }
  },
  methods: {
    dateIsBefore (before, after) {
      return moment(before).isBefore(after)
    },
    debounceMethod: debounce(function (callback) {
      return callback()
    }, 700),
    filterBigChoices (field = null, property = null) {
      const nestedProperty = (obj, path) => path.split('.').reduce((o, p) => o[p], obj)

      if (field && property) {
        if (this.bigChoices[field].search && this.bigChoices[field].search.length >= 3) {
          this.bigChoices[field].items = nestedProperty(this, property).filter(item =>
            item.name.toLowerCase().includes(this.bigChoices[field].search.toLowerCase())
          )
        } else {
          this.bigChoices[field].items = []
        }
      }
    },
    sumProperty (arr, prop) {
      /**
       * Sum a property (plain or nested) of an array of objects
       * If that finds in the prop, it will reduce automatically
       * @param arr objects array to iterate
       * @param prop string name property
       * @return number
       */
      if (!arr.length || !prop) {
        return 0
      }

      return arr.reduce((total, item) => {
        const props = prop.split('.')
        let value = item

        for (let i = 0; i < props.length; i++) {
          if (Array.isArray(value)) {
            // reduce array of objects: we add each one recursively from this position
            const key = props.slice(i).join('.')

            return total + value.reduce((sum, el) => {
              return sum + this.sumProperty([el], key)
            }, 0)
          } else {
            value = value?.[props[i]]
          }
        }

        return total + (parseFloat(value) || 0)
      }, 0)
    },
    toClipboard (string) {
      /**
       * Copy text to clipboard
       * @param string text to copy
       */
      this.$copyText(string)
      .then(() => {
        this.$dialog.message.info('Copiado')
      })
    },
    parseErrorsDict (dict, translate = 'api') {
      const containerTemplate = template('<div><%= content %></div>')
      let output = []

      each(dict, (value, key) => {
        const fieldTemplate = template(
          '<span class="d-block mb-2 body-1 font-weight-medium"><%= name %></span><%= content %>'
        )
        const valueTemplate = template(
          '<span class="d-block"><%= value %></span>'
        )

        let plainValue = null
        let listValue = null
        let content = null

        if (isString(value)) {
          plainValue = value
        } else if (isArray(value)) {
          if (isString(value[0])) {
            plainValue = value.join(' ')
          } else {
            listValue = this.parseErrorsList(value)
          }
        }

        if (plainValue) {
          if (!plainValue.includes(' ')) {
            plainValue = this.$t(`${translate}.${plainValue}`)
          }

          content = valueTemplate({ value: plainValue })
        } else if (listValue) {
          content = listValue
        }

        if (content) {
          if (translate) {
            if (key.search(/_(\d+)$/) !== -1) {
              const values = key.split('_')
              key = this.$t(`${translate}.${values[0]}`, {number: values[1]})
            } else {
              key = this.$t(`${translate}.${key}`)
            }
          }
          output.push(fieldTemplate({
            name: capitalize(key),
            content: content
          }))
        }
      })

      output = output.join(' ')

      if (output) {
        output = containerTemplate({ content: output })
      }

      return output
    },
    parseErrorsList (list, translate = 'api') {
      const containerTemplate = template('<%= content %>')
      let output = []

      each(list, (dict, index) => {
        if (!isEmpty(dict)) {
          output.push(this.parseErrorsDict(dict, translate))
        }
      })

      output = output.join(' ')

      if (output) {
        output = containerTemplate({ content: output })
      }

      return output
    },
    parseErrors (data, translate = 'api') {
      if (isArray(data)) {
        return this.parseErrorsList(data, translate)
      } else {
        return this.parseErrorsDict(data, translate)
      }
    },
    startTour (id) {
      /**
       * @param id Number intercom tour id
       */
      if (process.env.NODE_ENV === 'production') {
        window.Intercom('startTour', id)
      }
    },
    intercomNewMessage (message = '') {
      if (process.env.NODE_ENV === 'production') {
        window.Intercom('showNewMessage', message)
      }
    },
    formatNumber (value, round = true, decimalPlaces = 2) {
      if (round) {
        return Math.round(value)
      }
      return Math.round(value * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces)
    },
    trackEvent (event, context) {
      /**
       * track event triggered by user
       * it runs as long as the env is production
       */
      if (process.env.NODE_ENV !== 'production') {
        return false
      }

      this.$analytics.track(event, context)
    }
  }
}
